import { css } from 'aphrodite';
import { IMergedContactInfo, IRevertImportResult, IRevertSkippedContact } from '../../../models/AdminModels';
import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { Modal } from '../../../web/components/Modal';
import { DangerIcon } from '../../../web/components/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '../../../web/components/svgs/icons/SuccessCheckIcon';
import { type RevertResultState } from './revertOutcome';
import { styleSheet } from './styles';

export { resolveRevertError, revertResultFromOperation } from './revertOutcome';
export type { RevertResultState } from './revertOutcome';

interface SummaryRow {
	count: number;
	label: string;
}

function getSummaryRows(result: IRevertImportResult): SummaryRow[] {
	const rows: SummaryRow[] = [
		{ count: result.contactsDeleted, label: 'contacts archived' },
		{ count: result.contactsRestored, label: 'contacts restored to pre-import state' },
		{ count: result.contactsMergedReverted, label: 'merges reversed' },
		{ count: result.policiesArchived, label: 'policies archived' },
		{ count: result.householdsDeleted, label: 'households deleted' },
		{ count: result.notesDeleted, label: 'notes deleted' },
		{ count: result.automationsCancelled, label: 'automations cancelled' },
		{ count: result.companiesDeleted, label: 'companies deleted' },
	];
	const filtered = rows.filter(r => r.count > 0);
	if (result.jobDeleted) {
		filtered.push({ count: 1, label: 'import job deleted' });
	}
	return filtered;
}

function FailedMergeItem({ merge }: { merge: IMergedContactInfo }) {
	return (
		<li className={css(styleSheet.warningContactId)}>
			{merge.masterContactId}: {merge.failureReason}
		</li>
	);
}

function formatSkippedContactName(contact: IRevertSkippedContact): string {
	const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
	return name || contact.id;
}

function getSkippedContactEmail(contact: IRevertSkippedContact): string | undefined {
	return contact.primaryEmail?.value ?? contact.emailAddresses?.[0]?.value;
}

function SkippedContactItem({ contact }: { contact: IRevertSkippedContact }) {
	const name = formatSkippedContactName(contact);
	const email = getSkippedContactEmail(contact);
	return (
		<li className={css(styleSheet.skippedContact)}>
			<span className={css(styleSheet.skippedContactName)}>{name}</span>
			{contact.jobTitle && <span className={css(styleSheet.skippedContactTitle)}> — {contact.jobTitle}</span>}
			{email && <div className={css(styleSheet.skippedContactEmail)}>{email}</div>}
		</li>
	);
}

interface Props {
	isOpen: boolean;
	onRequestClose(): void;
	state: RevertResultState | null;
}

export function RevertResultModal({ isOpen, onRequestClose, state }: Props) {
	if (!state) {
		return null;
	}

	return (
		<Modal isOpen={isOpen} onRequestClose={onRequestClose} useDefaultHeader>
			<div className={css(styleSheet.container)}>
				{state.kind === 'error' ? (
					<ErrorContent message={state.message} onClose={onRequestClose} />
				) : (
					<SuccessContent state={state} onClose={onRequestClose} />
				)}
			</div>
		</Modal>
	);
}

function ErrorContent({ message, onClose }: { message: string; onClose(): void }) {
	return (
		<>
			<div className={css(styleSheet.title, styleSheet.titleError)}>
				<DangerIcon className={css(styleSheet.titleIcon)} />
				Cannot Revert Import
			</div>
			<div className={css(styleSheet.errorSection)}>
				<div className={css(styleSheet.errorMessage)}>{message}</div>
			</div>
			<div className={css(styleSheet.footer)}>
				<Button variant='secondary' onClick={onClose}>
					Close
				</Button>
			</div>
		</>
	);
}

function SuccessContent({
	state,
	onClose,
}: {
	state: Extract<RevertResultState, { result: IRevertImportResult }>;
	onClose(): void;
}) {
	const { result } = state;
	const summaryRows = getSummaryRows(result);
	const failedMerges = (result.mergedContacts ?? []).filter(m => !m.reverted);
	const hasRevertedMerges = (result.mergedContacts ?? []).some(m => m.reverted);
	const failedMergeCount = result.contactsMergeRevertFailed || failedMerges.length;

	return (
		<>
			<div
				className={css(styleSheet.title, state.kind === 'warning' ? styleSheet.titleWarning : styleSheet.titleSuccess)}
			>
				{state.kind === 'warning' ? (
					<DangerIcon
						className={css(styleSheet.titleIcon)}
						backgroundColor={THEME.colors.warning.dark}
						borderColor={THEME.colors.warning.dark}
					/>
				) : (
					<SuccessCheckIcon className={css(styleSheet.titleIcon)} />
				)}
				{state.kind === 'warning' ? 'Import Reverted with Warnings' : 'Import Reverted Successfully'}
			</div>

			{summaryRows.length > 0 ? (
				<ul className={css(styleSheet.summaryList)}>
					{summaryRows.map(row => (
						<li key={row.label} className={css(styleSheet.summaryItem)}>
							<span className={css(styleSheet.summaryCount)}>{row.count}</span>
							{row.label}
						</li>
					))}
				</ul>
			) : state.kind === 'warning' ? null : (
				<div className={css(styleSheet.emptyFallback)}>The import was reverted. No data changes were needed.</div>
			)}

			{state.kind === 'warning' && (
				<div className={css(styleSheet.warningsSection)}>
					<div className={css(styleSheet.warningsTitle)}>Warnings</div>

					{result.contactsSkippedModifiedSinceImport > 0 && (
						<div className={css(styleSheet.warningItem)}>
							{result.contactsSkippedModifiedSinceImport} contact
							{result.contactsSkippedModifiedSinceImport !== 1 ? 's were' : ' was'} skipped because{' '}
							{result.contactsSkippedModifiedSinceImport !== 1 ? 'they have' : 'it has'} been modified by a user since
							the import:
							{result.skippedContacts?.length > 0 && (
								<ul className={css(styleSheet.warningContactList)}>
									{result.skippedContacts.map(contact => (
										<SkippedContactItem key={contact.id} contact={contact} />
									))}
								</ul>
							)}
						</div>
					)}

					{failedMergeCount > 0 && (
						<div className={css(styleSheet.warningItem)}>
							{failedMergeCount} merge{failedMergeCount !== 1 ? 's' : ''} could not be reversed
							{failedMerges.length > 0 ? (
								<>
									:
									<ul className={css(styleSheet.warningContactList)}>
										{failedMerges.map((m, index) => (
											<FailedMergeItem key={`${m.masterContactId}-${index}`} merge={m} />
										))}
									</ul>
								</>
							) : (
								'.'
							)}
						</div>
					)}
				</div>
			)}

			{hasRevertedMerges && (
				<div className={css(styleSheet.mergeNote)}>Note: Merged contact note references may need manual review.</div>
			)}

			<div className={css(styleSheet.footer)}>
				<Button variant='secondary' onClick={onClose}>
					Close
				</Button>
			</div>
		</>
	);
}
